<template>
  <div>
    <single-file-upload-handler-sidebar />
    <edit-contract-sidebar />
    <add-receipt-sidebar />
    <query-asaas-identifier-sidebar />
    <request-deletion-sidebar />
    <extraordinary-change-sidebar />
    <edit-snapshot-sidebar />
    <comment-sidebar />
    <edit-contract-owner-sidebar />
    <settings-table-contracts-sidebar />
    <contract-report-tabs />

    <!-- Filters -->
    <contracts-list-filters
      :spinner-active="loading"
      ref="filters"
      :search="search"
    />

    <!-- Dashboard consolidado dos filtros aplicados -->
    <div v-if="searched" class="mb-1">
      <div class="d-flex justify-content-end mb-50">
        <b-button
          size="sm"
          variant="flat-secondary"
          @click="dashboardExpanded = !dashboardExpanded"
        >
          <feather-icon
            :icon="dashboardExpanded ? 'EyeOffIcon' : 'EyeIcon'"
            class="mr-50"
          />
          {{ dashboardExpanded ? "Ocultar indicadores" : "Mostrar indicadores" }}
        </b-button>
      </div>
      <b-collapse v-model="dashboardExpanded">
        <!--
          O churn entra na MESMA grade dos cards de status, pelo slot `leading`,
          em vez de numa b-row própria: antes ele ocupava uma linha inteira
          sozinho e empurrava os indicadores para baixo. Usa o mesmo
          `totalizer-card` deles, então herda largura, altura casada e quebra de
          linha responsiva.
        -->
        <contracts-totalizer
          :totalizers="totalizers"
          :aggroupments="aggroupments"
          :filterStatus="filterByStatus"
          :loading="loading"
        >
          <template #leading>
            <b-col class="totalizer-card">
              <contracts-churn-totalizer :churn="churn" :loading="loading" />
            </b-col>
          </template>
        </contracts-totalizer>
      </b-collapse>
    </div>
    <!-- Table Container Card -->
    <b-card v-if="searched" no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options="{ search, searching, loading }">
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <!-- Search -->
          <b-col cols="12" md="6">
            <b-row class="d-flex align-items-center justify-content-end">
              <div class="mx-1">
                <b-button
                  variant="outline-secondary"
                  @click="openSettingsTableSidebar({ visible: true })"
                >
                  <feather-icon icon="SettingsIcon" class="mr-50" />
                  <span class="text-nowrap">Colunas</span>
                </b-button>
              </div>
              <div v-if="canCreateContract" class="mr-1">
                <b-button variant="primary" :to="{ name: 'contract-save' }">
                  <feather-icon icon="PlusCircleIcon" class="mr-50" />
                  <span class="text-nowrap">Novo contrato</span>
                </b-button>
              </div>
            </b-row>
          </b-col>
        </b-row>
      </div>
      <b-table
        hover
        :items="items"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="Não foram encontrados produtos para esta busca"
        :busy="loading"
        :no-local-sorting="true"
        :sort-by="sortField"
        @sort-changed="sort"
        @row-clicked="goToContractDetails"
      >
        <!--
          Totais vindos de column_totals, calculado no backend sobre todo o recorte
          filtrado, então não mudam ao paginar.

          O thClass de cada coluna é repassado porque thead-top é markup próprio e
          não herda as classes dos fields: sem isso o total apareceria em coluna
          oculta e a linha sairia desalinhada.
        -->
        <template v-if="columnTotals" #thead-top>
          <b-tr class="contracts-column-totals">
            <b-th
              v-for="column in tableColumns"
              :key="`total-${column.key}`"
              :class="column.thClass"
            >
              <span v-if="columnTotals[column.key] !== undefined">
                {{ columnTotals[column.key] | toCurrency }}
              </span>
              <span
                v-else-if="column.key === firstVisibleColumn"
                class="text-muted font-weight-bold"
              >
                Totais
              </span>
            </b-th>
          </b-tr>
        </template>
        <!-- Column: status -->
        <template #cell(contract_status_name)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">
              {{ data.item.contract_status_name }}
            </span>
          </div>
        </template>
        <!-- Column: Dias header -->
        <template #head(days_count)>
          <span>Dias</span>
          <b-button
            id="dias-column-help"
            variant="flat"
            class="btn-icon p-0 ml-25"
          >
            <feather-icon
              icon="HelpCircleIcon"
              size="14"
              class="text-muted cursor-pointer"
            />
          </b-button>
          <b-popover target="dias-column-help" triggers="focus" placement="top">
            <template #title>Regra da coluna Dias</template>
            <p class="mb-50">
              <b>Assinaturas:</b> dias desde o último pagamento recebido (data de conciliação). Caso não haja pagamentos, considera a data de criação do contrato.
            </p>
            <p class="mb-0">
              <b>Demais contratos:</b> dias no status atual. Caso não haja histórico de status, considera a data de criação do contrato.
            </p>
          </b-popover>
        </template>
        <!-- Column: Dias -->
        <template #cell(days_count)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">
              {{ data.item.days_count }}
            </span>
          </div>
        </template>
        <!-- Column: PN -->
        <template #cell(consultant_name)="data">
          <div class="text-wrap">
            <template v-if="data.item.consultant_name">
              <b-button
                :id="`consultant-pop-icon-${data.item.id}`"
                class="btn-icon"
                variant="flat"
                style="padding: 0"
              >
                <feather-icon
                  :id="`consultant-pop-icon-${data.item.id}`"
                  size="16"
                  icon="ChevronsUpIcon"
                  class="cursor-pointer center"
                />
              </b-button>
              <user-detail-popover
                :target="`consultant-pop-icon-${data.item.id}`"
                type="consultant"
                :id="data.item.id_consultant"
                :showDetailUrl="true"
                :showWhastapp="true"
              />
              <span class="align-text-top text-capitalize">
                {{ data.item.consultant_name }}
              </span>
            </template>
          </div>
        </template>
        <!-- Column: Cliente -->
        <template #cell(customer_name)="data">
          <div class="text-wrap">
            <template v-if="data.item.customer_name">
              <b-button
                :id="`customer-pop-icon-${data.item.id}`"
                class="btn-icon"
                variant="flat"
                style="padding: 0"
              >
                <feather-icon
                  size="16"
                  icon="ChevronsUpIcon"
                  class="cursor-pointer"
                />
              </b-button>
              <user-detail-popover
                :target="`customer-pop-icon-${data.item.id}`"
                type="customer"
                :id="data.item.id_customer"
                :showDetailUrl="true"
                :showWhastapp="true"
              />
              <span class="align-text-top text-capitalize">
                {{ data.item.customer_name }}
              </span>
            </template>
          </div>
        </template>
        <!-- Column: solução financeira -->
        <template #cell(financial_solution_name)="data">
          <div class="text-wrap">
            <template v-if="data.item.financial_solution_name">
              <b-button
                :id="`financial-solution-pop-icon-${data.item.id}`"
                class="btn-icon"
                variant="flat"
                style="padding: 0"
              >
                <feather-icon
                  size="16"
                  icon="ChevronsUpIcon"
                  class="cursor-pointer"
                />
              </b-button>
              <financial-solution-popover
                :target="`financial-solution-pop-icon-${data.item.id}`"
                :id="data.item.financial_solution_id"
              />
              <span class="align-text-top text-capitalize">
                {{ data.item.financial_solution_name }}
              </span>
            </template>
          </div>
        </template>
        <!-- Column: Ltv Bruto -->
        <template #cell(gross_value)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">{{
              data.item.gross_value | toCurrency
            }}</span>
          </div>
        </template>
        <!-- Column: Prêmio mensal -->
        <template #cell(monthly_premium)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">{{
              data.item.monthly_premium | toCurrency
            }}</span>
          </div>
        </template>
        <!-- Column: Valor da Cobertura -->
        <!-- Checagem de nulo, e não de truthiness: zero é diferente de ausência. -->
        <template #cell(coverage_value)="data">
          <div class="text-wrap">
            <span
              v-if="data.item.coverage_value !== null && data.item.coverage_value !== undefined"
              class="align-text-top"
            >{{ data.item.coverage_value | toCurrency }}</span>
            <span v-else class="align-text-top text-muted">-</span>
          </div>
        </template>
        <!-- Column: Especialista -->
        <template #cell(specialist_name)="data">
          <div class="text-wrap">
            <span v-if="data.item.specialist_name" class="align-text-top">{{
              data.item.specialist_name
            }}</span>
            <span v-else class="align-text-top text-muted">-</span>
          </div>
        </template>
        <!-- Column: Nível do Especialista -->
        <template #cell(specialist_level)="data">
          <div class="text-wrap">
            <span v-if="data.item.specialist_level" class="align-text-top">{{
              data.item.specialist_level
            }}</span>
            <span v-else class="align-text-top text-muted">-</span>
          </div>
        </template>
        <!-- Column: Data do report -->
        <template #cell(contract_date)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">{{
              data.item.contract_date | onlyDate
            }}</span>
          </div>
        </template>
        <!-- Column: Data de assinatura  -->
        <template #cell(signed_at)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">{{
              data.item.signed_at | onlyDate
            }}</span>
          </div>
        </template>
        <!-- Column: Actions -->
        <!--
          Só "Editar" e "Excluir" são bloqueados pelo fechamento da competência: ficam
          VISÍVEIS e desabilitados, com o motivo no tooltip. "Editar" segue liberado para trocar
          entre status válidos (`edit_mode = status_only`). O tooltip vai no <div> externo
          porque `.dropdown-item.disabled` tem `pointer-events: none` e não dispara hover.
        -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret right boundary="window">
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="data.item.consultant_status !== consultantStatus.ACTIVE"
              @click="updateOwner(data.item.id)"
            >
              <feather-icon icon="Edit3Icon" />
              <span class="align-middle ml-50">Editar dono do contrato</span>
            </b-dropdown-item>
            <b-dropdown-item @click="detailsNewTab(data.item.id)">
              <feather-icon icon="ExternalLinkIcon" />
              <span class="align-middle ml-50">Detalhes do contrato</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="showDownload(data.item)"
              @click="downloadContrato(data.item.id)"
            >
              <feather-icon icon="FileIcon" />
              <span class="align-middle ml-50">Baixar contrato</span>
            </b-dropdown-item>
            <div
              v-if="canUpdateContract"
              v-b-tooltip.hover.left="contractLockTooltip(data.item)"
            >
              <b-dropdown-item
                :disabled="!mayEdit(data.item)"
                @click="editContract(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Editar</span>
              </b-dropdown-item>
            </div>
            <b-dropdown-item
              v-if="mayChangeExtraordinarily(data.item)"
              @click="extraordinaryChange(data.item)"
            >
              <feather-icon icon="AlertOctagonIcon" />
              <span class="align-middle ml-50">Alteração extraordinária</span>
            </b-dropdown-item>
            <template v-if="canImportPayments">
              <b-dropdown-item @click="addReceipt(data.item)">
                <feather-icon icon="DollarSignIcon" />
                <span class="align-middle ml-50">Adicionar recebimento</span>
              </b-dropdown-item>

              <b-dropdown-item @click="queryAsaasId">
                <feather-icon icon="SearchIcon" />
                <span class="align-middle ml-50"
                  >Consultar identificador Asaas</span
                >
              </b-dropdown-item>
            </template>
            <div
              v-if="showBtDelete(data.item) && canDeleteContract"
              v-b-tooltip.hover.left="contractDeleteLockTooltip(data.item)"
            >
              <b-dropdown-item
                :disabled="!mayDelete(data.item)"
                @click="requestDeletion(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">Excluir</span>
              </b-dropdown-item>
            </div>
            <template v-if="canUpdateContract">
              <b-dropdown-item @click="editSnapshot(data.item)">
                <feather-icon icon="PieChartIcon" />
                <span class="align-middle ml-50">Solicitar alteração de snapshot</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="
                  isAssinatura(data.item.financial_solution_id) ||
                  isEP(data.item.financial_solution_id)
                "
                @click="updatePayments(data.item.id)"
              >
                <feather-icon icon="RefreshCcwIcon" />
                <span class="align-middle ml-50"
                  >Sincronizar pagamentos do contrato</span
                >
              </b-dropdown-item>
            </template>
            <b-dropdown-item @click="openFileChooser(data.item.id)">
              <feather-icon icon="PaperclipIcon" />
              <span class="align-middle ml-50">Anexar arquivo</span>
            </b-dropdown-item>
            <b-dropdown-item @click="addComment(data.item.id)">
              <feather-icon icon="MessageSquareIcon" />
              <span class="align-middle ml-50">Adicionar comentário</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canUpdateContract"
              @click="resetDaysStatus(data.item.id)"
            >
              <feather-icon icon="RefreshCcwIcon" />
              <span class="align-middle ml-50">Reiniciar contador de dias</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canUpdateImportedPayments"
              @click="reversalPayments(data.item.id)"
            >
              <feather-icon icon="RotateCcwIcon" />
              <span class="align-middle ml-50">Estornar comissões</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de
              {{ totalItems }} itens</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
  BPopover,
  BCollapse,
  BTr,
  BTh,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import useAppConfig from "@core/app-config/useAppConfig";
import { onlyDate, floatToMoney } from "@/helpers/converters";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { apiErrorToast } from "@/helpers/error_messages";
import { contractPermissions } from "@/mixins/contractPermissions";
import ContractsListFilters from "../components/ContractsListFilters";
import ContractReportTabs from "../components/ContractReportTabs.vue";
import ContractsTotalizer from "../components/ContractsTotalizer";
import ContractsChurnTotalizer from "../components/ContractsChurnTotalizer";
import {
  buildContractColumnTotals,
  firstVisibleContractColumnKey,
} from "../utils/contractColumnTotals";
import SettingsTableContractsSidebar from "../components/SettingsTableContractsSidebar";
import FinancialSolutionPopover from "../components/FinancialSolutionPopover";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import * as accountTypes from "@/modules/account/store/types";
import UserDetailPopover from "@/modules/shared/components/UserDetailPopover";
import SingleFileUploadHandlerSidebar from "@/modules/shared/components/SingleFileUploadHandlerSidebar";
import EditContractSidebar from "../components/EditContractSidebar";
import AddReceiptSidebar from "../components/AddReceiptSidebar";
import QueryAsaasIdentifierSidebar from "../components/QueryAsaasIdentifierSidebar";
import { onlyDigits } from "../../../helpers/converters";
import RequestDeletionSidebar from "../components/RequestDeletionSidebar";
import ExtraordinaryChangeSidebar from "../components/ExtraordinaryChangeSidebar";
import EditSnapshotSidebar from "../components/EditSnapshotSidebar";
import CommentSidebar from "@/modules/shared/components/CommentSidebar";
import EditContractOwnerSidebar from "../components/EditContractOwnerSidebar";
import { periodRange } from "../utils/contractPeriod";
import * as financial from "@/constants/financial";
import * as consultantStatus from "@/constants/consultant_status";
import { isLockedStatus } from "../utils/contractLock";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BPopover,
    BCollapse,
    BTr,
    BTh,
    vSelect,
    ContractsListFilters,
    ContractReportTabs,
    UserDetailPopover,
    ContractsTotalizer,
    ContractsChurnTotalizer,
    SettingsTableContractsSidebar,
    FinancialSolutionPopover,
    SingleFileUploadHandlerSidebar,
    EditContractSidebar,
    AddReceiptSidebar,
    QueryAsaasIdentifierSidebar,
    RequestDeletionSidebar,
    ExtraordinaryChangeSidebar,
    EditSnapshotSidebar,
    CommentSidebar,
    EditContractOwnerSidebar,
  },
  mixins: [contractPermissions],
  setup() {
    const { skin } = useAppConfig();

    return { toast: useToast(), skin };
  },
  data() {
    return {
      consultantStatus,
      loading: false,
      exporting: false,
      order: undefined,
      sortField: null,
      currentPage: 1,
      itemsPerPage: 1,
      searched: false,
      dashboardExpanded: true,
    };
  },
  computed: {
    ...mapGetters({
      contractSearchResult: types.CONTRACTS_SEARCH_RESULT,
      tableColumns: types.CONTRACTS_TABLE_COLUMNS,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
      user: accountTypes.USER,
    }),
    items: function () {
      return this.contractSearchResult?.data || [];
    },
    totalItems: function () {
      return this.contractSearchResult?.total || 0;
    },
    pageItemsFrom: function () {
      return this.contractSearchResult?.from || 0;
    },
    pageItemsTo: function () {
      return this.contractSearchResult?.to || 0;
    },
    totalizers: function () {
      return this.contractSearchResult?.totalizers || [];
    },
    aggroupments: function () {
      return this.contractSearchResult?.totalizers_aggroupments || [];
    },
    churn: function () {
      return this.contractSearchResult?.churn;
    },
    columnTotals: function () {
      return buildContractColumnTotals(this.contractSearchResult?.column_totals);
    },
    firstVisibleColumn: function () {
      return firstVisibleContractColumnKey(this.tableColumns);
    }
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    // O filtro filho já montou e aplicou os padrões (ex.: período do mês
    // corrente), então a primeira busca sai sem o usuário clicar em Buscar.
    this.search(1);
    this.getSettingsTableContracts().catch(() => {
      // A preferência de colunas é acessória: se falhar, a tabela continua
      // usável com todas as colunas visíveis, então não vale um toast de erro.
    });
  },
  methods: {
    ...mapActions({
      searchContract: types.SEARCH_CONTRACTS,
      exportContracts: types.EXPORT_CONTRACTS,
      attachContractFile: types.SAVE_ATTACHMENT,
      openFileUploadSidebar: sharedTypes.OPEN_FILE_UPLOAD_SIDEBAR,
      openEditContract: types.OPEN_EDIT_CONTRACT_SIDEBAR,
      openAddReceiptSidebar: types.OPEN_ADD_RECEIPT_SIDEBAR,
      openQueryAsaasIdSidebar: types.OPEN_QUERY_ASAAS_ID_SIDEBAR,
      openRequestDeletionSidebar: types.OPEN_REQUEST_DELETION_SIDEBAR,
      openExtraordinaryChangeSidebar: types.OPEN_EXTRAORDINARY_CHANGE_SIDEBAR,
      openEditSnapshot: types.OPEN_EDIT_SNAPSHOT_SIDEBAR,
      updateContractPayments: types.UPDATE_PAYMENTS,
      openCommentSidebar: sharedTypes.OPEN_COMMENT_SIDEBAR,
      saveComment: types.SAVE_COMMENT,
      resetDaysOnStatus: types.RESET_DAYS_ON_STATUS,
      reversalContractPayments: types.REVERSAL_CONTRACT_PAYMENTS,
      openEditOwnerSidebar: types.OPEN_EDIT_CONTRACT_OWNER_SIDEBAR,
      downloadOnContract: types.DOWNLOAD_ON_CONTRACT,
      openSettingsTableSidebar: types.OPEN_SETTINGS_TABLE_SIDEBAR,
      getSettingsTableContracts: types.GET_SETTINGS_TABLE_CONTRACTS,
    }),
    isEP(financial_solution_id) {
      return financial_solution_id == financial.EP;
    },
    isAssinatura(financial_solution_id) {
      return financial.isAssinatura(financial_solution_id);
    },
    showDownload(contract){
      return contract.has_autentique && contract.signed_at;
    },
    downloadContrato(id) {
      this.loading = true;

      this.downloadOnContract({
        id: id,
      })
        .then((response) => {
          if (response.data.contract_url) {
            setTimeout(() => {
              window.open(response.data.contract_url, "_blank").focus();
            });
          } else {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao baixar o contrato. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao baixar o contrato. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Abre em nova aba para o usuário não perder filtros, dashboard e página.
    // Clique na linha e "Detalhes do contrato" usam a mesma rotina.
    goToContractDetails(contract, index) {
      this.detailsNewTab(contract.id);
    },
    openFileChooser(contractId) {
      this.openFileUploadSidebar({
        title: "Anexo do contrato",
        description: `Escolha um documento para anexar ao contrato Nº ${contractId}`,
        id: contractId,
        saveAction: this.attachContractFile,
      });
    },
    // `closingException` só é preenchido quando a edição vem do fluxo de alteração
    // extraordinária: o id da exceção viaja junto com o PUT do contrato.
    editContract(contract, closingException) {
      const {
        id,
        proposal_number,
        id_contract_status,
        financial_solution_id,
        createdAt,
        signed_at,
        paid_at,
        deployed_at,
      } = contract;
      this.openEditContract({
        id,
        proposal_number,
        id_contract_status,
        financial_solution_id,
        createdAt: this.getDateString(createdAt),
        signed_at: this.getDateString(signed_at),
        paid_at: this.getDateString(paid_at),
        deployed_at: this.getDateString(deployed_at),
        closing_exception_id: closingException?.exception_id,
        edit_mode: contract.edit_mode,
        allowed_status_ids: contract.allowed_status_ids,
        currentPage: this.currentPage ? this.currentPage : 1,
        saveAction: this.search,
      });
    },
    // Passo 1: coleta a justificativa. Passo 2 (o `proceed`): a edição real do contrato.
    extraordinaryChange(contract) {
      this.openExtraordinaryChangeSidebar({
        id: contract.id,
        competence: contract.competence,
        lock_reason: contract.lock_reason,
        proceed: (result) => this.editContract(contract, result),
      });
    },
    getDateString(date) {
      return onlyDate(date);
    },
    addReceipt(contract) {
      this.openAddReceiptSidebar({
        id: contract.id,
        currentPage: this.currentPage ? this.currentPage : 1,
        saveAction: this.search,
      });
    },
    queryAsaasId() {
      this.openQueryAsaasIdSidebar();
    },
    requestDeletion(contractId) {
      this.openRequestDeletionSidebar({
        id: contractId,
        currentPage: this.currentPage ? this.currentPage : 1,
        saveAction: this.search,
      });
    },
    editSnapshot(contract) {
      this.openEditSnapshot({
        id: contract.id,
        consultant_id: contract.id_consultant,
      });
    },
    addComment(contractId) {
      this.openCommentSidebar({
        id: contractId,
        title: "Adicionar comentário ao contrato " + contractId,
        saveAction: this.saveComment,
      });
    },
    // Contratos em status terminal saem do menu. O bloqueio por competência fechada
    // NÃO esconde o item: ele fica desabilitado com o motivo no tooltip.
    showBtDelete(contractData) {
      return !isLockedStatus(contractData);
    },
    updatePayments(contractId) {
      this.updateContractPayments(contractId)
        .then((response) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "O contrato foi sincronizado com sucesso!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Ocorreu um erro ao sincronizar o contrato. Entre em contato com o setor de TI."
            ),
          });
        });
    },
    filterByStatus(status) {
      this.$refs.filters.status = status;
      this.search(1);
    },
    search(currentPage) {
      this.searched = true;
      this.loading = true;
      this.currentPage = currentPage;
      const {
        customer,
        customerCPF,
        contractId,
        status,
        contractDate,
        dateReference,
        initialPaymentDate,
        endPaymentDate,
        franchise,
        consultant,
        specialist,
        daysOnStatus,
        product,
        type,
        partnerCompany,
        financialSolution,
        structureOption,
        partnerStructureOption,
      } = this.$refs.filters;
      const contract_date = periodRange(contractDate);
      this.searchContract({
        customer,
        customerCPF: onlyDigits(customerCPF),
        contractId,
        status,
        initialDate: contract_date.startDate,
        endDate: contract_date.endDate,
        dateReference,
        initialPaymentDate,
        endPaymentDate,
        franchise,
        consultant,
        specialist,
        daysOnStatus,
        product,
        type,
        partnerCompany,
        financialSolution,
        structureOption,
        partnerStructureOption,
        order: this.order,
        limit: this.itemsPerPage,
        currentPage: Number.isInteger(currentPage) ? currentPage : 1,
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar os contratos. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportData() {
      this.exporting = true;
      const {
        customer,
        customerCPF,
        status,
        contractDate,
        dateReference,
        initialPaymentDate,
        endPaymentDate,
        franchise,
        consultant,
        specialist,
        daysOnStatus,
        product,
        type,
        partnerCompany,
        financialSolution,
        structureOption,
        partnerStructureOption,
      } = this.$refs.filters;
      const contract_date = periodRange(contractDate);
      this.exportContracts({
        customer,
        customerCPF: onlyDigits(customerCPF),
        status,
        initialDate: contract_date.startDate,
        endDate: contract_date.endDate,
        dateReference,
        initialPaymentDate,
        endPaymentDate,
        franchise,
        consultant,
        specialist,
        daysOnStatus,
        product,
        type,
        partnerCompany,
        financialSolution,
        structureOption,
        partnerStructureOption,
        order: this.order,
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar os contratos. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    sort(event) {
      //clear in third click
      if (!event.sortDesc && this.sortField == event.sortBy) {
        this.order = undefined;
        this.sortField = null;
      } else {
        var direction = event.sortDesc ? " DESC" : " ASC";
        this.order = event.sortBy + direction;
        this.sortField = event.sortBy;
      }
      this.search();
    },
    detailsNewTab(id) {
      var routeData = this.$router.resolve({
        name: "contract-details-summary",
        params: { id },
      });
      setTimeout(() => {
        window.open(routeData.href, "_blank");
      });
    },
    resetDaysStatus(id) {
      this.resetDaysOnStatus({ id })
        .then((response) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "Os dias foram reiciados com sucesso!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Ocorreu um erro ao reiniciar os dias. Entre em contato com o setor de TI."
            ),
          });
        })
        .finally(() => {
          this.search(this.currentPage);
        });
    },
    reversalPayments(id) {
      this.$swal({
        title: "Tem certeza?",
        text: `Deseja estornar todas as comissões do contrato? A operação não poderá ser desfeita.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        background: `${this.skin === "dark" ? "#283046" : ""}`,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
          htmlContainer: `${this.skin === "dark" ? "text-white" : ""}`,
        },
      }).then((result) => {
        if (result.value) {
          this.reversalContractPayments({ id })
            .then((response) => {
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Sucesso",
                  text: "Todas as comissões do contrato foram estornadas.",
                  icon: "CoffeeIcon",
                  variant: "success",
                },
              });
            })
            .catch((error) => {
              this.toast({
                component: ToastificationContent,
                props: apiErrorToast(
                  error,
                  "Ocorreu um erro ao estornar as comissões. Entre em contato com o setor de TI."
                ),
              });
            })
            .finally(() => {
              this.search(this.currentPage);
            });
        }
      });
    },
    updateOwner(contractId) {
      this.openEditOwnerSidebar({
        id: contractId,
        saveAction: this.search,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
// Sem `scoped` porque a linha nasce dentro do thead renderizado pelo b-table.
.contracts-column-totals th {
  font-size: 0.9rem;
  white-space: nowrap;
  text-transform: none;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
